/* ==========================================================================
   Young Grandpa - product media gallery, desktop mechanic
   --------------------------------------------------------------------------
   At 1024px and up the gallery stops being a horizontal carousel and becomes a
   vertical scroller. <slideshow-container> is a fixed frame with the images
   stacked inside it, so the next image rises from the bottom as the wheel
   turns, tied to scroll distance. That movement is native scroll, not
   animation. At the first and last image the frame drops out of the scroll
   chain so the browser's own scrolling carries on into the page - native
   chaining alone loses that gesture to Chrome's scroll latching. See onWheel.

   Layout is in assets/yg-custom.css. The part of it that matters here is that
   <slideshow-slides> loses its own overflow, so the Scroller that
   assets/slideshow.js binds to it never sees a scroll event and never fights
   the frame. The cost is the theme's own scrolling: select() still does its
   bookkeeping - counter text, thumbnail aria-selected, arrow state - but its
   scroll call now lands on an element that cannot scroll. This file supplies
   the scroll and leaves the bookkeeping where it is.

   - Thumbnail and arrow clicks: assets/component.js delegates on:click from a
     capture listener on document, so by the time our bubble handler runs the
     theme has already worked out and applied the new index. We scroll the
     frame to it.
   - Thumbnail hover does the same jump without a click. There is no delegation
     for it, so this file applies the index as well as the scroll. Hover, the
     arrows and the wheel are three independent ways through the same gallery.
   - The counter and the thumbnail highlight follow an IntersectionObserver
     while the shopper scrolls freely.
   - Mouse drag stays blocked. assets/slideshow.js would set the [dragging]
     attribute on mousedown, which base.css turns into pointer-events: none.

   Nothing here applies below 1024px, where the horizontal swipe carousel is
   exactly as it was.
   ========================================================================== */

(function () {
  'use strict';

  var DESKTOP = '(min-width: 1024px)';
  var FRAME = 'slideshow-container';
  var THUMBNAIL = '.slideshow-controls__thumbnail';
  var ARROW = '.slideshow-control--next, .slideshow-control--previous';
  var INTERACTIVE = 'model-viewer, video, iframe, button, a, input, label, dialog';
  var RATIOS = [0, 0.2, 0.4, 0.6, 0.8, 1];
  /* Sub-pixel slack: scrollTop lands on fractional values at fractional device
     pixel ratios, so an exact === comparison never reports the end. */
  var EDGE = 1;
  /* Quiet time that marks the end of a wheel or trackpad gesture, momentum
     included. Long enough not to split one flick into two. */
  var GESTURE_GAP = 200;

  var desktop = window.matchMedia(DESKTOP);
  var bound = new WeakSet();
  var observer = new MutationObserver(schedule);
  var scheduled = false;

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      init();
    });
  }

  function init() {
    var galleries = document.querySelectorAll('media-gallery');

    for (var i = 0; i < galleries.length; i++) {
      var gallery = galleries[i];

      /* assets/media-gallery.js swaps the whole <media-gallery> element out on
         a variant change, which takes these listeners with it. Watching the
         parent lets us bind whatever replaces it. */
      if (gallery.parentElement) observer.observe(gallery.parentElement, { childList: true });

      if (bound.has(gallery)) continue;
      bound.add(gallery);
      attach(gallery);
    }
  }

  /**
   * The slides the theme considers live, in the order slideshow.current indexes
   * them. Falls back to the DOM while the custom element is still upgrading.
   *
   * @param {Element} slideshow
   * @param {Element} frame
   * @returns {Element[]}
   */
  function slidesOf(slideshow, frame) {
    var live = slideshow && slideshow.slides;
    if (live && live.length) return live;

    return Array.prototype.filter.call(frame.querySelectorAll('slideshow-slide'), function (slide) {
      return !slide.hasAttribute('hidden') || slide.hasAttribute('reveal');
    });
  }

  /**
   * Scrolls the frame so that a slide sits at its top edge. Worked out from
   * rects rather than offsetTop, which depends on where the nearest positioned
   * ancestor happens to be.
   *
   * Called only from a click and from the initial-slide jump, never while the
   * shopper is scrolling. `behavior` is passed per call because the frame's CSS
   * deliberately carries no scroll-behavior.
   *
   * @param {Element} container
   * @param {Element} child
   * @param {ScrollBehavior} behavior
   */
  function scrollTo(container, child, behavior) {
    var top = container.scrollTop + child.getBoundingClientRect().top - container.getBoundingClientRect().top;

    container.scrollTo({ top: top, behavior: behavior });
  }

  /**
   * The frame is as tall as one image, so its ratio has to be the product's
   * own. The theme writes each slide's ratio into --media-preview-ratio when
   * the gallery is set to Adapt; measuring the first slide's box covers the
   * fixed-ratio settings too. assets/yg-custom.css carries a 2:3 default, which
   * is what nearly all of the catalogue is shot at.
   *
   * @param {Element} frame
   */
  function sizeFrame(frame) {
    var slide = frame.querySelector('slideshow-slide');
    if (!slide) return;

    var box = slide.getBoundingClientRect();
    if (!box.width || !box.height) return;

    /* Writing the same value back would resize nothing, but it still hands the
       ResizeObserver below a reason to run again. */
    var ratio = (box.width / box.height).toFixed(4);
    if (frame.style.getPropertyValue('--yg-frame-ratio') === ratio) return;

    frame.style.setProperty('--yg-frame-ratio', ratio);
  }

  /**
   * next() and previous() step by the number of slides the theme believes are
   * visible, and it measures that on the x axis (Slideshow#visibleSlides in
   * assets/slideshow.js). Stacked vertically every slide shares the same x
   * extent, so all of them read as visible and the arrows would jump the length
   * of the gallery. Shadow the two getters with a plain one-slide step, leaving
   * the wrap that select() applies at the ends intact.
   *
   * @param {Element} slideshow
   * @param {string} name
   * @param {number} step
   */
  function patchStep(slideshow, name, step) {
    var original = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(slideshow), name);
    if (!original || typeof original.get !== 'function') return;

    Object.defineProperty(slideshow, name, {
      configurable: true,
      get: function () {
        if (!desktop.matches) return original.get.call(this);
        return this.current + step;
      },
    });
  }

  /**
   * @param {Element} gallery
   */
  function attach(gallery) {
    var frame = gallery.querySelector(FRAME);
    if (!frame) return;

    var slideshow = frame.closest('slideshow-component');
    var ratios = new Map();
    /* Boundary-release state, per gesture rather than per event. */
    var lastWheel = 0;
    var released = false;
    var reclaimTimer = null;

    /**
     * Stops assets/slideshow.js from starting a drag, and stops the browser
     * from starting a native image drag in its place.
     *
     * @param {MouseEvent} event
     */
    function blockDrag(event) {
      if (!desktop.matches) return;

      var target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('slideshow-slides')) return;
      if (target.closest(INTERACTIVE)) return;

      event.preventDefault();
      event.stopPropagation();
    }

    /**
     * The theme has already applied the new index by the time this runs, so
     * there is no index to work out here - only a frame to move.
     *
     * @param {MouseEvent} event
     */
    function onClick(event) {
      if (!desktop.matches || !slideshow) return;

      var target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest(THUMBNAIL + ', ' + ARROW)) return;

      var slides = slidesOf(slideshow, frame);
      var slide = slides[slideshow.current];
      if (slide) scrollTo(frame, slide, 'smooth');
    }

    /**
     * Boundary release at the first and last image.
     *
     * Native overscroll chaining is not enough on its own. Chrome latches a
     * continuous gesture - a trackpad two-finger scroll, or wheel momentum -
     * to whichever scroller it started on, so once the frame has reached its
     * end the rest of that gesture is still swallowed by it. The page only
     * moves once the shopper stops and starts again, or takes the cursor off
     * the gallery, which is the trap this fixes. Discrete wheel clicks chain
     * on their own and never reach the branch below.
     *
     * Only the two boundaries are intercepted. Everything between them falls
     * through untouched to native scroll, which is what keeps the reveal tied
     * to scroll distance and free of snap.
     *
     * @param {WheelEvent} event
     */
    function onWheel(event) {
      if (!desktop.matches) return;

      var now = event.timeStamp || performance.now();
      /* A gap this long means fingers left the trackpad, or the momentum died:
         the next event starts a fresh gesture and the frame owns it again. */
      if (now - lastWheel > GESTURE_GAP) reclaim();
      lastWheel = now;

      window.clearTimeout(reclaimTimer);
      reclaimTimer = window.setTimeout(reclaim, GESTURE_GAP);

      /* Latched: the page has this gesture until it ends. Deliberately no
         re-check of the boundary, and no reading of where the cursor is - that
         is what stopped a small wiggle at the last image from flipping the
         decision back and forth. */
      if (released) return;

      var dy = event.deltaY;
      if (!dy) return;

      var max = frame.scrollHeight - frame.clientHeight;
      var past = dy > 0 ? frame.scrollTop >= max - EDGE : frame.scrollTop <= EDGE;
      if (!past) return;

      release();
    }

    /**
     * Hands the rest of the gesture to the page by taking the frame out of the
     * scroll chain entirely, rather than by preventing the event and moving the
     * page ourselves.
     *
     * This is the whole point: the browser keeps doing the scrolling, so what
     * the shopper gets is its own wheel smoothing and its own trackpad
     * momentum - identical to scrolling anywhere else on the page, because it
     * is the same code path. Calling preventDefault and driving window.scrollBy
     * per event replaced that easing with a series of discrete jumps, which is
     * what the stutter was.
     *
     * scrollTop survives overflow: hidden - the content still overflows, it is
     * just no longer user-scrollable - so the gallery stays on the image it was
     * showing.
     */
    function release() {
      if (released) return;
      released = true;
      frame.style.overflowY = 'hidden';
    }

    function reclaim() {
      if (!released) return;
      released = false;
      frame.style.overflowY = '';
    }

    /**
     * Hover-to-navigate. A thumbnail click gets its index applied by the
     * theme's own on:click delegation, leaving onClick above only the scroll
     * to do; hover has no such delegation, so both halves happen here - the
     * index bookkeeping first, then the same scroll a click would ask for.
     *
     * Delegated from mouseover rather than mouseenter because mouseenter does
     * not bubble. mouseover then fires again for every child of the button the
     * pointer crosses (the <img>, the badge), which the index guard absorbs.
     *
     * Wheel scrolling is untouched: it moves the frame directly and the
     * IntersectionObserver below reports the result, so the two navigate the
     * same gallery independently.
     *
     * @param {MouseEvent} event
     */
    function onThumbnailHover(event) {
      if (!desktop.matches || !slideshow) return;

      var target = event.target;
      if (!(target instanceof Element)) return;

      var thumb = target.closest(THUMBNAIL);
      if (!thumb || !gallery.contains(thumb)) return;

      var thumbs = gallery.querySelectorAll(THUMBNAIL);
      var index = Array.prototype.indexOf.call(thumbs, thumb);
      if (index < 0 || index === slideshow.current) return;

      var slide = slidesOf(slideshow, frame)[index];
      if (!slide) return;

      slideshow.current = index;
      scrollTo(frame, slide, 'smooth');
    }

    /**
     * @param {IntersectionObserverEntry[]} entries
     */
    function onIntersect(entries) {
      if (!desktop.matches || !slideshow || typeof slideshow.next !== 'function') return;

      for (var i = 0; i < entries.length; i++) ratios.set(entries[i].target, entries[i].intersectionRatio);

      var best = null;
      var bestRatio = 0;
      ratios.forEach(function (ratio, slide) {
        /* Insertion order means a tie goes to the slide nearer the top. */
        if (ratio > bestRatio) {
          bestRatio = ratio;
          best = slide;
        }
      });
      if (!best) return;

      var index = slidesOf(slideshow, frame).indexOf(best);
      if (index < 0 || index === slideshow.current) return;

      /* Reporting only. Nothing in this path may scroll anything: a
         programmatic scroll landing mid-gesture is what makes a scroller catch.
         Slideshow's `current` setter is bookkeeping - counter text, thumbnail
         aria-selected, arrow state - and does not move the frame. */
      slideshow.current = index;
    }

    gallery.addEventListener('mousedown', blockDrag, true);
    gallery.addEventListener('click', onClick);
    gallery.addEventListener('mouseover', onThumbnailHover);
    /* Passive: this handler never calls preventDefault any more, and saying so
       keeps it off the browser's critical path for scrolling. */
    frame.addEventListener('wheel', onWheel, { passive: true });

    sizeFrame(frame);

    var slides = frame.querySelectorAll('slideshow-slide');
    var watcher = new IntersectionObserver(onIntersect, { root: frame, threshold: RATIOS });
    for (var i = 0; i < slides.length; i++) watcher.observe(slides[i]);

    /* A ResizeObserver rather than a resize listener: the frame's width is set
       by the grid, which the theme editor can change without the window moving. */
    new ResizeObserver(function () {
      sizeFrame(frame);
    }).observe(frame);

    if (!slideshow || !window.customElements) return;

    window.customElements.whenDefined('slideshow-component').then(function () {
      patchStep(slideshow, 'nextIndex', 1);
      patchStep(slideshow, 'previousIndex', -1);
      showInitialSlide();

      /* The theme rewrites initial-slide when a variant that pins a different
         image is picked and the gallery is morphed rather than replaced. */
      new MutationObserver(showInitialSlide).observe(slideshow, {
        attributes: true,
        attributeFilter: ['initial-slide'],
      });
    });

    function showInitialSlide() {
      if (!desktop.matches) return;

      var index = parseInt(slideshow.getAttribute('initial-slide') || '0', 10);
      if (!index) return;

      var slide = slidesOf(slideshow, frame)[index];
      if (slide) scrollTo(frame, slide, 'instant');
    }
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function () {
    observer.disconnect();
    init();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
